import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { getMenuGroups } from './menuConfig';

const SidebarContext = createContext(null);

/**
 * 节流函数
 */
const throttle = (fn, delay) => {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
};

/**
 * 侧边栏状态 Provider
 */
export const SidebarProvider = ({ children }) => {
  const location = useLocation();
  
  // 持久化状态
  const [collapsed, setCollapsed] = useLocalStorage('sidebar_collapsed', false);
  const [expandedGroups, setExpandedGroups] = useLocalStorage(
    'sidebar_expanded_groups',
    ['core', 'user-ops', 'transaction', 'content', 'system', 'platform'] // 默认全部展开
  );
  
  // 临时状态
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isMobile, setIsMobile] = useState(() => {
    // SSR 安全的初始值
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  const [drawerVisible, setDrawerVisible] = useState(false);
  
  // 用于跟踪用户是否手动设置过折叠状态
  const userCollapsedRef = useRef(false);
  
  // 响应式监听（带节流）
  useEffect(() => {
    const handleResize = throttle(() => {
      const width = window.innerWidth;
      const newIsMobile = width < 768;
      setIsMobile(newIsMobile);
      
      // 中等屏幕自动折叠（仅在用户未手动设置时）
      if (width < 992 && width >= 768 && !userCollapsedRef.current) {
        setCollapsed(true);
      }
      // 大屏幕恢复展开（仅在用户未手动设置时）
      if (width >= 992 && !userCollapsedRef.current) {
        setCollapsed(false);
      }
    }, 100);
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setCollapsed]);
  
  // 路由变化时自动展开对应分组
  useEffect(() => {
    const currentPath = location.pathname;
    getMenuGroups().forEach(group => {
      const hasActiveItem = group.items.some(item => {
        if (currentPath === item.key || currentPath.startsWith(item.key + '/')) {
          return true;
        }
        if (item.children) {
          return item.children.some(child => 
            currentPath === child.key || currentPath.startsWith(child.key + '/')
          );
        }
        return false;
      });
      
      if (hasActiveItem && !expandedGroups.includes(group.key)) {
        setExpandedGroups(prev => [...prev, group.key]);
      }
    });
  }, [location.pathname, expandedGroups, setExpandedGroups]);
  
  // 切换分组展开状态
  const toggleGroup = useCallback((groupKey) => {
    setExpandedGroups(prev => 
      prev.includes(groupKey)
        ? prev.filter(k => k !== groupKey)
        : [...prev, groupKey]
    );
  }, [setExpandedGroups]);
  
  // 切换侧边栏折叠状态（用户手动操作）
  const toggleCollapsed = useCallback(() => {
    userCollapsedRef.current = true; // 标记用户手动操作
    setCollapsed(prev => !prev);
  }, [setCollapsed]);
  
  // 打开抽屉
  const openDrawer = useCallback(() => {
    setDrawerVisible(true);
  }, []);
  
  // 关闭抽屉
  const closeDrawer = useCallback(() => {
    setDrawerVisible(false);
  }, []);
  
  const value = {
    // 状态
    collapsed,
    expandedGroups,
    searchKeyword,
    isMobile,
    drawerVisible,
    // 方法
    setCollapsed,
    setExpandedGroups,
    setSearchKeyword,
    setDrawerVisible,
    toggleGroup,
    toggleCollapsed,
    openDrawer,
    closeDrawer,
  };
  
  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

/**
 * 使用侧边栏上下文的 Hook
 */
export const useSidebarContext = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarContext must be used within SidebarProvider');
  }
  return context;
};

export default SidebarContext;
